import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { EventType } from '@app/shared/constants-server';
import type { RentalCancellation } from '@app/shared/_models/sales';
import { CLOSED_LINE, lineClosed } from '../rental-billing.labels';
import { tooManyDecisions } from '../rental-billing.service';
import { RentalDecisionDialogsService } from '../rental-decision-dialogs.service';
import { RentalDecisionEvent, RentalDecisionTyp, decisionCancel } from '../rental-decision-events';
import type { CancelDecisionDialogData, CancelTarget } from '../rental-dialogs.contract';
import type { RentalLifecycleStore } from '../rental-lifecycle.store';
import { RentalEffectComponent } from '../effect/rental-effect.component';
import { RentalDecisionDialogBase, cancelledText, lineName } from './rental-decision-dialog.base';

interface TargetRow { key: string; lineId: number; id: number; name: string; detail: string; gone: string | null; }

function keyOf(t: { lineId: number; id: number }): string {
  return `${t.lineId}:${t.id}`;
}

// Entrée visée, lue dans le tableau de son type ; undefined si la vue ne la porte plus.
function entryOf(store: RentalLifecycleStore, typ: RentalDecisionTyp, lineId: number, id: number): { cancelled?: RentalCancellation | null } | undefined {
  const d = store.line(lineId)?.decisions;
  switch (typ) {
    case EventType.RENTAL_INTERRUPTION: return d?.interruptions.find((e) => e.id === id);
    case EventType.RENTAL_WINDOW: return d?.window.find((e) => e.id === id);
    case EventType.RENTAL_PRESCRIPTION: return d?.prescriptions.find((e) => e.id === id);
    case EventType.RENTAL_SALE_CANCELLATION: return store.sale(lineId)?.cancellations?.find((e) => e.id === id);
  }
  return undefined;
}

// Annulation motivée d'une décision sur les cibles cochées : elle reste visible, barrée (E.9).
@Component({
  selector: 'app-rental-cancel-decision-dialog',
  templateUrl: './rental-cancel-decision-dialog.component.html',
  styleUrls: ['./rental-cancel-decision-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatDialogModule, MatProgressSpinnerModule, RentalEffectComponent],
})
export class RentalCancelDecisionDialogComponent extends RentalDecisionDialogBase<CancelDecisionDialogData> {
  protected readonly dialogs = inject(RentalDecisionDialogsService);

  reason = '';
  checked = new Set<string>(this.data.targets.filter((t) => t.checked).map(keyOf));
  rows: TargetRow[] = [];
  errors: string[] = [];
  focusIds: number[] = [];
  scope: string | null = null;
  canSave = false;

  constructor() {
    super();
    this.update();
  }

  protected update(): void {
    this.rows = this.data.targets.map((t) => this.rowOf(t));
    for (const r of this.rows) if (r.gone) this.checked.delete(r.key);
    const sel = this.rows.filter((r) => this.checked.has(r.key));
    this.focusIds = [...new Set(sel.map((r) => r.lineId))];
    this.scope = sel.length ? '· ' + sel.map((r) => r.name).join(', ') : null;
    this.errors = [];
    if (!sel.length) this.errors.push('Aucune entrée cochée.');
    if (!this.reason.trim()) this.errors.push("Motif d'annulation obligatoire.");
    const evs = this.events();
    const many = tooManyDecisions(evs.length);
    if (many) this.errors.push(many);
    this.canSave = !this.saving && !!this.store.view && evs.length > 0 && this.errors.length === 0;
    this.plan(this.store.view ? evs : null);
  }

  private rowOf(t: CancelTarget): TargetRow {
    const l = this.store.line(t.lineId) ?? this.store.sale(t.lineId);
    const e = entryOf(this.store, this.data.typ, t.lineId, t.id);
    const memo = l?.memo?.trim();
    const was = e?.cancelled ? cancelledText(this.store, e.cancelled) : null;
    const gone = !this.store.view ? null : !e ? 'entrée absente de la ligne' : was ? 'déjà a' + was.slice(1) : lineClosed(l) ? CLOSED_LINE : null;
    return {
      key: keyOf(t), lineId: t.lineId, id: t.id,
      name: l ? lineName(l) : `ligne ${t.lineId}`,
      detail: [memo || null, l ? `ligne ${l.seq}` : null, `entrée n° ${t.id}`].filter((s) => !!s).join(' · '),
      gone,
    };
  }

  private events(): RentalDecisionEvent[] {
    return this.rows.filter((r) => this.checked.has(r.key)).map((r) => decisionCancel(this.data.typ, this.data.orderId, r.lineId, r.id, this.reason));
  }

  toggle(key: string, on: boolean): void {
    if (on) this.checked.add(key);
    else this.checked.delete(key);
    this.update();
  }

  setReason(v: string): void {
    this.reason = v;
    this.update();
  }

  confirm(): void {
    if (this.canSave) this.send(this.events(), 'Annulation');
  }
}
